import React from 'react';
import { motion } from 'framer-motion';
import { 
  Target,
  Users,
  GraduationCap,
  Heart,
  Briefcase,
  Calendar,
  Globe,
  Vote,
  BookOpen,
  Monitor,
  Database,
  CheckCircle
} from 'lucide-react';

const ObjectsAndFunctionsPage = () => {
  const objectives = [
    {
      icon: Target,
      text: 'खत्री (ब्रह्मक्षत्रिय) समाज के केंद्रीय संगठन के रूप में कार्य करना।',
      color: 'orange'
    },
    {
      icon: Users,
      text: 'समाज में समन्वयक की भूमिका में कार्य करना।',
      color: 'blue'
    },
    {
      icon: GraduationCap,
      text: 'समाज के सामाजिक, सांस्कृतिक, शैक्षिक, आर्थिक और नैतिक विकास के लिए विभिन्न योजनाएँ बनाना और उनका क्रियान्वयन करना।',
      color: 'green'
    },
    {
      icon: Heart,
      text: 'ब्रह्मक्षत्रिय खत्री समाज के सभी सदस्यों के बीच एकता, अखंडता और बंधुत्व को सुदृढ़ करना।',
      color: 'pink'
    },
    {
      icon: Briefcase,
      text: 'समाज में विवाह, शिक्षा, रोजगार और कल्याण से संबंधित सहायता कार्यक्रम संचालित करना।',
      color: 'purple'
    },
    {
      icon: Users,
      text: 'युवाओं में नेतृत्व गुण, नैतिकता और सामाजिक उत्तरदायित्व की भावना का विकास करना।',
      color: 'indigo'
    },
    {
      icon: Calendar,
      text: 'समय-समय पर सामुदायिक उत्थान के लिए परिचय सम्मेलन, सम्मेलन, कार्यशालाएँ और सेमिनार आयोजित करना।',
      color: 'teal'
    },
    {
      icon: Globe,
      text: 'हिंगलाज शक्तिपीठ, लसबेला, पाकिस्तान की व भारत में भी तीर्थयात्राओं का आयोजन करना।',
      color: 'amber'
    },
    {
      icon: Vote,
      text: 'समाज में राजनीतिक जागरुकता पैदा करना। सरकार की सार्वजनिक कल्याण योजनाओं का लाभ अधिकतम सदस्यों को दिलाने का प्रयास करना।',
      color: 'red'
    },
    {
      icon: BookOpen,
      text: 'समाज के इतिहास, सांस्कृतिक परम्पराओं व रीति-रिवाजों को प्रोत्साहित व प्रकाशित करना।',
      color: 'cyan'
    },
    {
      icon: Monitor,
      text: 'सोसाइटी की वेबसाइट बनाना व अपडेट करते रहना।',
      color: 'slate'
    },
    {
      icon: Database,
      text: 'समाज के सदस्यों और समाज से संबंधित डेटा और जानकारी एकत्र व प्रकाशित करना।',
      color: 'emerald'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      orange: { bg: 'bg-orange-100', icon: 'text-orange-600', border: 'border-orange-200' },
      blue: { bg: 'bg-blue-100', icon: 'text-blue-600', border: 'border-blue-200' },
      green: { bg: 'bg-green-100', icon: 'text-green-600', border: 'border-green-200' },
      pink: { bg: 'bg-pink-100', icon: 'text-pink-600', border: 'border-pink-200' },
      purple: { bg: 'bg-purple-100', icon: 'text-purple-600', border: 'border-purple-200' },
      indigo: { bg: 'bg-indigo-100', icon: 'text-indigo-600', border: 'border-indigo-200' },
      teal: { bg: 'bg-teal-100', icon: 'text-teal-600', border: 'border-teal-200' },
      amber: { bg: 'bg-amber-100', icon: 'text-amber-600', border: 'border-amber-200' },
      red: { bg: 'bg-red-100', icon: 'text-red-600', border: 'border-red-200' },
      cyan: { bg: 'bg-cyan-100', icon: 'text-cyan-600', border: 'border-cyan-200' },
      slate: { bg: 'bg-slate-100', icon: 'text-slate-600', border: 'border-slate-200' },
      emerald: { bg: 'bg-emerald-100', icon: 'text-emerald-600', border: 'border-emerald-200' }
    };
    return colors[color] || colors.orange;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="gradient-orange py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-white/80 text-sm mb-6">
            <a href="/" className="hover:text-white">Home</a>
            <span>›</span>
            <a href="#" className="hover:text-white">About Mahasabha</a>
            <span>›</span>
            <span>Objects and Functions</span>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Objects and Functions
            </h1>
            <p className="text-2xl text-white/95 font-medium mb-2">
              उद्देश्य व कार्य
            </p>
            <p className="text-lg text-white/90 leading-relaxed">
              ब्रह्मक्षत्रिय मारवाड़ी खत्री एसोसिएशन (महासभा) के उद्देश्य एवं कार्य
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-br from-primary/5 to-orange-50 rounded-2xl p-8 border border-primary/10 mb-12"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center">
              <Target className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">महासभा के उद्देश्य व कार्य</h2>
              <p className="text-gray-500">Objects and Functions of Mahasabha</p>
            </div>
          </div>
          <p className="text-gray-700 leading-relaxed">
            ब्रह्मक्षत्रिय मारवाड़ी खत्री एसोसिएशन (महासभा) समाज के समग्र विकास के लिए निम्नलिखित उद्देश्यों व कार्यों के प्रति प्रतिबद्ध है:
          </p>
        </motion.div>

        {/* Objectives Grid */}
        <div className="grid gap-4 md:gap-6">
          {objectives.map((objective, index) => {
            const colorClasses = getColorClasses(objective.color);
            const IconComponent = objective.icon;
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className={`flex items-start gap-4 p-5 bg-white rounded-xl border ${colorClasses.border} shadow-sm hover:shadow-md transition-shadow`}
              >
                <div className={`w-12 h-12 ${colorClasses.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <IconComponent className={`w-6 h-6 ${colorClasses.icon}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <p className="text-gray-700 text-lg leading-relaxed">
                      {objective.text}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 bg-gradient-to-br from-orange-500 to-primary rounded-2xl p-8 text-white text-center"
        >
          <h2 className="text-2xl font-bold mb-4">
            🙏 समाज सेवा में सहभागी बनें
          </h2>
          <p className="text-white/90 leading-relaxed max-w-2xl mx-auto mb-6">
            महासभा के इन उद्देश्यों को पूर्ण करने में आपका सहयोग अपेक्षित है। 
            सदस्य बनकर समाज के विकास में अपना योगदान दें।
          </p>
          <a
            href="/members"
            className="inline-flex items-center gap-2 bg-white text-primary px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors shadow-lg"
          >
            <Users className="w-5 h-5" />
            सदस्यता के लिए यहाँ क्लिक करें
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default ObjectsAndFunctionsPage;
